import { useState, type ReactNode } from 'react'
import { Box, Typography } from '@mui/material'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import { FS_XS, FS_SM } from '../common/theme'

export interface TaClickZoneProps {
  // Click handler - fires when the band (or wrapper, in children mode) is
  // clicked. The whole component is otherwise inert; pair it with a parent-
  // owned modal / file picker / navigation if you need an effect.
  onClick?: () => void

  // Visible band - caller can override the icon / label / sub-label and the
  // band sx (border thickness, padding, etc.). When children are provided
  // the outer wrapper picks up `wrapperSx` instead.
  icon?: ReactNode
  label?: string
  subLabel?: string
  sx?: any
  wrapperSx?: any

  // When children are provided, the click handler attaches to the outer
  // wrapper so clicks anywhere over the wrapper (not just the dashed band)
  // are accepted. The dashed band still renders at the top as the visible
  // affordance. Without children the dashed band itself is the click area.
  children?: ReactNode

  // Hide the dashed band visual while keeping the click wiring active -
  // useful when the caller has already staged content and wants to suppress
  // the affordance.
  showZone?: boolean

  testId?: string
}

// Click-only twin of TaDropZone. Same dashed-band look (border, hover
// transitions, icon/label slots) but with no drag / drop / file-input
// wiring. Use this for empty-state CTAs where the action is purely a
// click (e.g. "click to add a section", "click to pick a template").
export function TaClickZone({
  onClick,
  icon, label = 'Click to choose', subLabel,
  sx, wrapperSx, children, showZone = true, testId,
}: TaClickZoneProps) {

  const [isHovering, setIsHovering] = useState(false)

  const handleEnter = () => setIsHovering(true)
  const handleLeave = () => setIsHovering(false)

  const band = showZone ? (
    <Box
      data-testid={testId}
      onClick={children ? undefined : onClick}
      onMouseEnter={children ? undefined : handleEnter}
      onMouseLeave={children ? undefined : handleLeave}
      sx={{
        border: 2, borderStyle: 'dashed',
        borderColor: isHovering ? 'primary.main' : 'divider',
        borderRadius: 1, py: 2, px: 2,
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75, justifyContent: 'center',
        cursor: 'pointer',
        bgcolor: isHovering ? 'action.selected' : 'action.hover',
        transition: 'border-color 0.15s, background-color 0.15s',
        '&:hover': { borderColor: 'primary.light', bgcolor: 'action.selected' },
        ...sx,
      }}
    >
      {icon ?? <CloudUploadOutlinedIcon sx={{ fontSize: 28, opacity: 0.5 }} />}
      <Typography variant="caption" sx={{ opacity: 0.65, fontSize: FS_SM, textAlign: 'center' }}>
        {label}
      </Typography>
      {subLabel && (
        <Typography variant="caption" sx={{ opacity: 0.4, fontSize: FS_XS }}>
          {subLabel}
        </Typography>
      )}
    </Box>
  ) : null

  // Wrapper mode - outer Box absorbs clicks anywhere over the children area.
  if (children) {
    return (
      <Box
        onClick={onClick}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, ...wrapperSx }}
      >
        {band}
        {children}
      </Box>
    )
  }

  // Standalone - the dashed band itself is the click area.
  return band
}

export default TaClickZone
